import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Loader2, 
  FileText, 
  Lock, 
  RefreshCw, 
  BookOpen,
  CheckCircle,
  Shield,
  Clock,
  Mail,
  Phone,
  ChevronRight,
  AlertCircle,
  CreditCard,
  Users,
  Globe,
  Award,
  Calendar,
  Download,
  HelpCircle,
  ArrowRight
} from 'lucide-react';
import axios from 'axios';
import { Breadcrumb } from '@/components/common/Breadcrumb';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Terms of Use Page
export function TermsOfUsePage() {
  const sections = [
    {
      id: 'acceptance',
      title: '1. Acceptance of Terms',
      icon: CheckCircle,
      content: `By accessing and using the GRC Educators platform ("Service"), you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to abide by these terms, please do not use this Service. These terms apply to all visitors, users, and others who access or use the Service.`
    },
    {
      id: 'services',
      title: '2. Description of Services',
      icon: Globe,
      content: `GRC Educators provides online educational content, webinars, seminars, and training materials focused on Governance, Risk, and Compliance (GRC) topics.`,
      list: [
        'Live and recorded webinars with industry experts',
        'CPE-accredited training programs',
        'Certification preparation courses',
        'Industry-specific compliance training',
        'Corporate training solutions and group licensing'
      ]
    },
    {
      id: 'registration',
      title: '3. User Registration & Account',
      icon: Users,
      content: `To access certain features of our Service, you must register for an account. You agree to:`,
      list: [
        'Provide accurate, current, and complete information',
        'Maintain and promptly update your account information',
        'Keep your password secure and confidential',
        'Accept responsibility for all activities under your account',
        'Notify us immediately of any unauthorized use'
      ]
    },
    {
      id: 'payment',
      title: '4. Payment Terms',
      icon: CreditCard,
      content: `All fees are quoted in US Dollars unless otherwise stated. Payment is required before accessing paid content. We accept major credit cards and other payment methods as displayed on our checkout page.`,
      list: [
        'Prices are subject to change with reasonable notice',
        'Corporate accounts may have custom billing arrangements',
        'Refunds are processed according to our Refund Policy',
        'Failed payments may result in suspended access'
      ]
    },
    {
      id: 'intellectual',
      title: '5. Intellectual Property',
      icon: Shield,
      content: `All content on GRC Educators, including but not limited to text, graphics, logos, videos, audio recordings, and software, is the property of GRC Educators or its content suppliers and is protected by international copyright laws. You may not reproduce, distribute, modify, or create derivative works without explicit written permission.`
    },
    {
      id: 'conduct',
      title: '6. User Conduct',
      icon: AlertCircle,
      content: `You agree not to:`,
      list: [
        'Share your account credentials with others',
        'Redistribute or resell our content without permission',
        'Use automated systems to access the Service',
        'Interfere with the proper working of the Service',
        'Attempt to gain unauthorized access to any portion of the Service',
        'Use the Service for any unlawful purpose'
      ]
    },
    {
      id: 'cpe',
      title: '7. CPE Credits',
      icon: Award,
      content: `CPE credits are issued according to NASBA guidelines. Attendance and participation requirements must be met to receive credits. We maintain records as required by regulatory bodies. Credits are subject to verification and may be revoked if requirements are not met.`
    },
    {
      id: 'liability',
      title: '8. Limitation of Liability',
      icon: FileText,
      content: `GRC Educators shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the Service. Our total liability shall not exceed the amount paid by you for the Service in the twelve months preceding the claim.`
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="terms-of-use-page">
      {/* Hero */}
      <section className="hero-canvas border-b border-zinc-200 pt-28 pb-16 relative overflow-hidden">
        <div className="container-custom relative z-10">
          <Breadcrumb />
          <div className="max-w-3xl">
            <span className="mono-label inline-flex items-center gap-2 text-[11px] text-zinc-600 bg-white border border-zinc-200 rounded-full px-3 py-1 mb-5">
              <FileText className="w-3 h-3" />
              Legal
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-[-0.03em] text-zinc-950 mb-4">
              Terms of Use
            </h1>
            <p className="text-zinc-500 text-lg mb-4">
              Please read these terms carefully before using our platform.
            </p>
            <p className="text-zinc-400 text-sm">
              Last Updated: February 2026 | Effective Date: February 2026
            </p>
          </div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="py-8 bg-white border-b border-slate-200">
        <div className="container-custom">
          <div className="flex flex-wrap gap-2">
            {sections.map((section) => (
              <a 
                key={section.id}
                href={`#${section.id}`}
                className="px-4 py-2 bg-slate-100 hover:bg-navy-100 text-slate-700 hover:text-navy-700 rounded-lg text-sm font-medium transition-colors"
              >
                {section.title.split('.')[1]?.trim() || section.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto space-y-8">
            {sections.map((section) => (
              <Card key={section.id} id={section.id} className="border-0 shadow-lg overflow-hidden scroll-mt-32">
                <CardContent className="p-0">
                  <div className="flex items-center gap-4 p-6 bg-slate-50 border-b border-slate-100">
                    <div className="w-12 h-12 rounded-xl bg-navy-100 flex items-center justify-center">
                      <section.icon className="w-6 h-6 text-navy-600" />
                    </div>
                    <h2 className="font-heading text-xl font-semibold text-navy-700">
                      {section.title}
                    </h2>
                  </div>
                  <div className="p-6">
                    <p className="text-slate-600 leading-relaxed mb-4">
                      {section.content}
                    </p>
                    {section.list && (
                      <ul className="space-y-3">
                        {section.list.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-slate-600">{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Contact Section */}
            <Card className="border-0 shadow-lg bg-navy-800 text-white">
              <CardContent className="p-8">
                <h3 className="font-heading text-xl font-semibold mb-4">Questions About Our Terms?</h3>
                <p className="text-slate-300 mb-6">
                  If you have any questions about these Terms of Use, please contact our legal team.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a href="mailto:support@grceducators.us" className="flex items-center gap-2 text-gold-400 hover:text-gold-300">
                    <Mail className="w-4 h-4" />
                    support@grceducators.us
                  </a>
                  <a href="tel:+17134019995" className="flex items-center gap-2 text-slate-300 hover:text-white">
                    <Phone className="w-4 h-4" />
                    713-401-9995
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}

// Privacy Policy Page
export function PrivacyPolicyPage() {
  const sections = [
    {
      id: 'collection',
      title: 'Information We Collect',
      icon: Users,
      items: [
        { label: 'Personal Information', desc: 'Name, email address, phone number, company name, job title' },
        { label: 'Payment Information', desc: 'Billing address and payment card details (processed securely via Stripe)' },
        { label: 'Professional Information', desc: 'Credentials, certifications, and professional background' },
        { label: 'Usage Data', desc: 'Pages visited, features used, webinars attended, completion status' },
        { label: 'Device Information', desc: 'IP address, browser type, operating system, device identifiers' },
      ]
    },
    {
      id: 'usage',
      title: 'How We Use Your Information',
      icon: Globe,
      items: [
        { label: 'Service Delivery', desc: 'Provide access to webinars, track progress, issue certificates' },
        { label: 'Communication', desc: 'Send confirmations, reminders, and important updates' },
        { label: 'Personalization', desc: 'Recommend relevant content based on your interests' },
        { label: 'CPE Compliance', desc: 'Maintain records required by NASBA and other accreditation bodies' },
        { label: 'Improvement', desc: 'Analyze usage patterns to enhance our platform' },
      ]
    },
    {
      id: 'sharing',
      title: 'Information Sharing',
      icon: Shield,
      items: [
        { label: 'Service Providers', desc: 'Payment processors, email services, hosting providers' },
        { label: 'CPE Authorities', desc: 'NASBA and state boards as required for credit verification' },
        { label: 'Legal Requirements', desc: 'When required by law or to protect our rights' },
        { label: 'Business Transfers', desc: 'In connection with mergers or acquisitions' },
      ],
      note: 'We do NOT sell your personal information to third parties.'
    },
    {
      id: 'rights',
      title: 'Your Rights',
      icon: CheckCircle,
      items: [
        { label: 'Access', desc: 'Request a copy of your personal data' },
        { label: 'Correction', desc: 'Update or correct inaccurate information' },
        { label: 'Deletion', desc: 'Request deletion of your data (subject to legal requirements)' },
        { label: 'Opt-Out', desc: 'Unsubscribe from marketing communications' },
        { label: 'Portability', desc: 'Export your data in a machine-readable format' },
      ]
    },
    {
      id: 'security',
      title: 'Data Security',
      icon: Lock,
      items: [
        { label: 'Encryption', desc: 'SSL/TLS encryption for all data transmission' },
        { label: 'Access Controls', desc: 'Role-based access to sensitive information' },
        { label: 'Monitoring', desc: 'Regular security assessments and audits' },
        { label: 'Training', desc: 'Employee training on data protection practices' },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-policy-page">
      {/* Hero */}
      <section className="hero-canvas border-b border-zinc-200 pt-28 pb-16 relative overflow-hidden">
        <div className="container-custom relative z-10">
          <Breadcrumb />
          <div className="max-w-3xl">
            <span className="mono-label inline-flex items-center gap-2 text-[11px] text-zinc-600 bg-white border border-zinc-200 rounded-full px-3 py-1 mb-5">
              <Lock className="w-3 h-3" />
              Privacy
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-[-0.03em] text-zinc-950 mb-4">
              Privacy Policy
            </h1>
            <p className="text-zinc-500 text-lg mb-4">
              Your privacy is important to us. Learn how we collect, use, and protect your information.
            </p>
            <p className="text-zinc-400 text-sm">
              Last Updated: February 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {sections.map((section) => (
                <Card key={section.id} id={section.id} className="border-0 shadow-lg scroll-mt-32">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                        <section.icon className="w-6 h-6 text-purple-600" />
                      </div>
                      <h2 className="font-heading text-xl font-semibold text-navy-700">
                        {section.title}
                      </h2>
                    </div>
                    <div className="space-y-4">
                      {section.items.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                          <div className="w-2 h-2 rounded-full bg-purple-500 mt-2 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-navy-700">{item.label}</p>
                            <p className="text-slate-600 text-sm">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {section.note && (
                      <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                        <p className="text-green-700 font-medium flex items-center gap-2">
                          <CheckCircle className="w-5 h-5" />
                          {section.note}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card className="border-0 shadow-lg sticky top-24">
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-navy-700 mb-4">Quick Links</h3>
                  <div className="space-y-2">
                    {sections.map((section) => (
                      <a 
                        key={section.id}
                        href={`#${section.id}`}
                        className="flex items-center gap-2 p-3 rounded-lg text-slate-600 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                      >
                        <section.icon className="w-4 h-4" />
                        {section.title}
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg bg-navy-700 text-white">
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-3">Contact Privacy Team</h3>
                  <p className="text-slate-300 text-sm mb-4">
                    Have questions about your data?
                  </p>
                  <a href="mailto:support@grceducators.us" className="text-gold-400 hover:text-gold-300 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    support@grceducators.us
                  </a>
                  <a href="tel:+17134019995" className="text-slate-300 hover:text-white flex items-center gap-2 mt-2">
                    <Phone className="w-4 h-4" />
                    713-401-9995
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// Refund Policy Page
export function RefundPolicyPage() {
  const policies = [
    {
      type: 'Live Webinars',
      icon: Calendar,
      color: 'bg-blue-100 text-blue-600',
      rules: [
        { timing: 'More than 72 hours before', action: 'Full refund', highlight: true },
        { timing: '24-72 hours before', action: '50% refund or credit for future webinar' },
        { timing: 'Less than 24 hours', action: 'No refund, but may transfer to a colleague' },
        { timing: 'No-show', action: 'No refund' },
      ]
    },
    {
      type: 'Recorded Webinars',
      icon: Download,
      color: 'bg-green-100 text-green-600',
      rules: [
        { timing: 'Before access', action: 'Full refund within 7 days of purchase', highlight: true },
        { timing: 'After access', action: 'No refund once content is accessed' },
        { timing: 'Technical issues', action: 'We will resolve issues or provide refund' },
      ]
    },
    {
      type: 'Multi-Day Seminars',
      icon: Users,
      color: 'bg-purple-100 text-purple-600',
      rules: [
        { timing: 'More than 14 days before', action: 'Full refund minus $50 processing fee', highlight: true },
        { timing: '7-14 days before', action: '50% refund' },
        { timing: 'Less than 7 days', action: 'No refund, substitutions allowed' },
      ]
    },
    {
      type: 'Digital Downloads / USB',
      icon: Download,
      color: 'bg-orange-100 text-orange-600',
      rules: [
        { timing: 'Before download/shipping', action: 'Full refund', highlight: true },
        { timing: 'After download/shipping', action: 'No refund due to digital nature' },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="refund-policy-page">
      {/* Hero */}
      <section className="hero-canvas border-b border-zinc-200 pt-28 pb-16 relative overflow-hidden">
        <div className="container-custom relative z-10">
          <Breadcrumb />
          <div className="max-w-3xl">
            <span className="mono-label inline-flex items-center gap-2 text-[11px] text-zinc-600 bg-white border border-zinc-200 rounded-full px-3 py-1 mb-5">
              <RefreshCw className="w-3 h-3" />
              Policy
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-[-0.03em] text-zinc-950 mb-4">
              Refund &amp; Cancellation Policy
            </h1>
            <p className="text-zinc-500 text-lg mb-4">
              We want you to be completely satisfied with your purchase. Here's our refund policy.
            </p>
            <p className="text-zinc-400 text-sm">
              Last Updated: February 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            {/* Policy Cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {policies.map((policy, idx) => (
                <Card key={idx} className="border-0 shadow-lg overflow-hidden">
                  <CardContent className="p-0">
                    <div className={`flex items-center gap-4 p-5 ${policy.color.replace('text-', 'bg-').replace('-600', '-50')} border-b border-slate-100`}>
                      <div className={`w-12 h-12 rounded-xl ${policy.color.split(' ')[0]} flex items-center justify-center`}>
                        <policy.icon className={`w-6 h-6 ${policy.color.split(' ')[1]}`} />
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-navy-700">
                        {policy.type}
                      </h3>
                    </div>
                    <div className="p-5 space-y-3">
                      {policy.rules.map((rule, ridx) => (
                        <div 
                          key={ridx} 
                          className={`flex items-center justify-between p-3 rounded-lg ${rule.highlight ? 'bg-green-50 border border-green-200' : 'bg-slate-50'}`}
                        >
                          <span className="text-slate-600 text-sm">{rule.timing}</span>
                          <span className={`text-sm font-medium ${rule.highlight ? 'text-green-700' : 'text-navy-700'}`}>
                            {rule.action}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* How to Request */}
            <Card className="border-0 shadow-lg mb-8">
              <CardContent className="p-8">
                <h3 className="font-heading text-xl font-semibold text-navy-700 mb-6">
                  How to Request a Refund
                </h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center p-6 bg-slate-50 rounded-xl">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-navy-100 flex items-center justify-center">
                      <Mail className="w-6 h-6 text-navy-600" />
                    </div>
                    <h4 className="font-semibold text-navy-700 mb-2">Email</h4>
                    <a href="mailto:support@grceducators.us" className="text-gold-600 hover:text-gold-700">
                      support@grceducators.us
                    </a>
                  </div>
                  <div className="text-center p-6 bg-slate-50 rounded-xl">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-navy-100 flex items-center justify-center">
                      <Phone className="w-6 h-6 text-navy-600" />
                    </div>
                    <h4 className="font-semibold text-navy-700 mb-2">Phone</h4>
                    <span className="text-slate-600">713-401-9995</span>
                  </div>
                  <div className="text-center p-6 bg-slate-50 rounded-xl">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-navy-100 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-navy-600" />
                    </div>
                    <h4 className="font-semibold text-navy-700 mb-2">Processing Time</h4>
                    <span className="text-slate-600">5-10 business days</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Special Circumstances */}
            <Card className="border-0 shadow-lg bg-amber-50 border-l-4 border-amber-500">
              <CardContent className="p-6">
                <h3 className="font-heading text-lg font-semibold text-navy-700 mb-3 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-amber-600" />
                  Special Circumstances
                </h3>
                <p className="text-slate-600 mb-4">
                  We may make exceptions to this policy in cases of:
                </p>
                <div className="flex flex-wrap gap-3">
                  {['Medical emergencies', 'Natural disasters', 'Military deployment', 'Other extenuating circumstances'].map((item, idx) => (
                    <span key={idx} className="px-4 py-2 bg-white rounded-lg text-slate-700 text-sm shadow-sm">
                      {item}
                    </span>
                  ))}
                </div>
                <p className="text-slate-500 text-sm mt-4">
                  Documentation may be required. Contact us to discuss your situation.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}

// Articles Page
export function ArticlesPage() {
  const categories = [
    {
      title: 'Regulatory Updates',
      description: 'Stay current with the latest regulatory changes',
      icon: FileText,
      color: 'from-blue-500 to-blue-700',
      count: 'Coming Soon'
    },
    {
      title: 'Best Practices',
      description: 'Learn from industry leaders',
      icon: Award,
      color: 'from-green-500 to-green-700',
      count: 'Coming Soon'
    },
    {
      title: 'Industry Insights',
      description: 'Sector-specific compliance guidance',
      icon: Globe,
      color: 'from-purple-500 to-purple-700',
      count: 'Coming Soon'
    },
    {
      title: 'Professional Development',
      description: 'Advance your GRC career',
      icon: Users,
      color: 'from-orange-500 to-orange-700',
      count: 'Coming Soon'
    },
  ];

  const upcomingFeatures = [
    { icon: Download, title: 'Downloadable Templates', desc: 'Compliance checklists and frameworks' },
    { icon: BookOpen, title: 'Video Tutorials', desc: 'Step-by-step guides from experts' },
    { icon: CheckCircle, title: 'Interactive Tools', desc: 'Compliance assessment calculators' },
    { icon: Users, title: 'Expert Interviews', desc: 'Insights from industry leaders' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pt-20" data-testid="articles-page">
      {/* Hero */}
      <section className="bg-navy-900 py-16">
        <div className="container-custom">
          <div className="max-w-3xl">
            <Badge className="bg-gold-500/20 text-gold-300 border border-gold-500/30 mb-4">
              <BookOpen className="w-3 h-3 mr-1" />
              Resources
            </Badge>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Articles & Resources
            </h1>
            <p className="text-slate-300 text-lg">
              Explore our collection of articles, whitepapers, and resources designed to keep you informed about the latest trends in Governance, Risk, and Compliance.
            </p>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-12">
        <div className="container-custom">
          <h2 className="font-heading text-2xl font-bold text-navy-700 mb-8 text-center">
            Browse by Category
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {categories.map((cat, idx) => (
              <Card key={idx} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden group cursor-pointer">
                <CardContent className="p-0">
                  <div className={`h-32 bg-zinc-900 flex items-center justify-center`}>
                    <cat.icon className="w-12 h-12 text-white/80 group-hover:scale-110 transition-transform" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-lg font-semibold text-navy-700 mb-2">
                      {cat.title}
                    </h3>
                    <p className="text-slate-500 text-sm mb-3">{cat.description}</p>
                    <Badge variant="secondary" className="text-xs">{cat.count}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Coming Soon Section */}
          <Card className="border-0 shadow-xl bg-navy-900 text-white overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <Badge className="bg-gold-500 text-navy-800 mb-4">Coming Soon</Badge>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                    Resource Library Launching Soon
                  </h3>
                  <p className="text-slate-300 mb-6">
                    We're building a comprehensive resource library with articles, templates, tutorials, and tools to help you excel in your GRC career.
                  </p>
                  <Link to="/register">
                    <Button className="bg-gold-500 hover:bg-gold-600 text-navy-800 font-semibold rounded-xl">
                      Get Notified
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {upcomingFeatures.map((feature, idx) => (
                    <div key={idx} className="p-4 bg-white/10 rounded-xl backdrop-blur-sm">
                      <feature.icon className="w-8 h-8 text-gold-400 mb-3" />
                      <h4 className="font-semibold text-white mb-1">{feature.title}</h4>
                      <p className="text-slate-400 text-sm">{feature.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Newsletter Signup */}
          <div className="mt-12 text-center">
            <h3 className="font-heading text-xl font-semibold text-navy-700 mb-3">
              Stay Updated
            </h3>
            <p className="text-slate-600 mb-6 max-w-lg mx-auto">
              Subscribe to our newsletter to receive new articles, industry news, and exclusive content directly to your inbox.
            </p>
            <Link to="/contact">
              <Button variant="outline" className="rounded-xl border-navy-200 text-navy-700 hover:bg-navy-50">
                Subscribe to Newsletter
                <Mail className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default {
  TermsOfUsePage,
  PrivacyPolicyPage,
  RefundPolicyPage,
  ArticlesPage
};
